// types/auth.ts

export interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  roles: string[];
  isActive: boolean; // ✅ AGREGADO: Estado activo del usuario
  passwordSet: boolean; // ✅ CORREGIDO: Cambiar de hasPassword a passwordSet (coincide con backend)
  createdAt: string; // ✅ AGREGADO: Fecha de creación (ISO string)
  updatedAt: string; // ✅ AGREGADO: Fecha de última actualización
  lastLogin?: string; // ✅ AGREGADO: Último login (opcional)
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token: string;
  user: User;
}

// ✅ NUEVO: Interface para crear usuarios (usado en formularios)
export interface CreateUserRequest {
  username: string;
  fullName: string;
  email: string;
  password?: string; // Opcional
  roles: string[];
  isActive: boolean;
  sendWelcomeEmail?: boolean; // Opcional, por defecto true
}

// ✅ NUEVO: Interface para actualizar usuarios
export interface UpdateUserRequest {
  fullName: string;
  email: string;
  password?: string; // Opcional
  roles: string[];
  isActive: boolean;
}

// ✅ NUEVO: Interface para respuesta de reseteo de contraseña
export interface PasswordResetResponse {
  success: boolean;
  message: string;
  token?: string;
}

// ✅ NUEVO: Interface para validación de token
export interface ValidateTokenResponse {
  success: boolean; // ✅ CORREGIDO: usar 'success' (coincide con backend)
  message: string;
  email?: string;
}

// ✅ NUEVO: Interface para solicitud de reseteo
export interface ForgotPasswordRequest {
  email: string;
}

// ✅ NUEVO: Interface para resetear contraseña
export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

// Definir roles del sistema
export const UserRole = {
  ADMIN: 'ADMIN',
  SUPERVISOR: 'SUPERVISOR', 
  USER: 'USER',
  PHONE_AGENT: 'PHONE_AGENT',
  COMPSYCH_SYNC: 'COMPSYCH_SYNC' // Sincronización ComPsych: Dashboard EAP, cuentas, clientes, casos y citas
} as const;

export type UserRole = typeof UserRole[keyof typeof UserRole];

// Permisos por funcionalidad
export const Permission = {
  // Navegación básica
  VIEW_DASHBOARD: 'VIEW_DASHBOARD',
  VIEW_ACCOUNTS: 'VIEW_ACCOUNTS',
  VIEW_CLIENTS: 'VIEW_CLIENTS',
  VIEW_REFERRALS: 'VIEW_REFERRALS',
  VIEW_CLAIMS: 'VIEW_CLAIMS',
  
  // Acciones
  CREATE_ENTRY: 'CREATE_ENTRY',
  
   // ✅ NUEVOS: Permisos del módulo telefónico
  VIEW_PHONE_DASHBOARD: 'VIEW_PHONE_DASHBOARD',
  REGISTER_PHONE_CALL: 'REGISTER_PHONE_CALL',
  VIEW_OWN_PHONE_CALLS: 'VIEW_OWN_PHONE_CALLS',
  ASSIGN_SPECIALIST: 'ASSIGN_SPECIALIST',
  UPLOAD_EVIDENCE: 'UPLOAD_EVIDENCE',

  // WhatsApp: bandeja compartida e integración con Intake
  VIEW_WHATSAPP_INBOX: 'VIEW_WHATSAPP_INBOX',
  MANAGE_WHATSAPP_ASSIGNMENTS: 'MANAGE_WHATSAPP_ASSIGNMENTS',

  // Administración
  ACCESS_SETTINGS: 'ACCESS_SETTINGS',
  MANAGE_USERS: 'MANAGE_USERS',
  MANAGE_SPECIALISTS: 'MANAGE_SPECIALISTS',
  MANAGE_SURVEY_TEMPLATES: 'MANAGE_SURVEY_TEMPLATES'
} as const;

export type Permission = typeof Permission[keyof typeof Permission];

// Mapeo de roles a permisos - ✅ PERSONALIZADO POR ROL
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // 👤 USER: Acceso básico limitado (sin Dashboard EAP: solo ADMIN y COMPSYCH_SYNC)
  [UserRole.USER]: [
    Permission.VIEW_CLIENTS,
    Permission.VIEW_REFERRALS,
    Permission.CREATE_ENTRY
    // ❌ NO puede ver: Dashboard EAP, Accounts, Claims, Settings
  ],

  // 📞 PHONE_AGENT: Acceso específico al módulo telefónico y WhatsApp
  [UserRole.PHONE_AGENT]: [
    Permission.VIEW_PHONE_DASHBOARD,
    Permission.REGISTER_PHONE_CALL,
    Permission.VIEW_OWN_PHONE_CALLS,
    Permission.ASSIGN_SPECIALIST,
    Permission.UPLOAD_EVIDENCE,
    Permission.VIEW_WHATSAPP_INBOX
    // ❌ NO puede ver: Dashboard EAP, Accounts, Claims, Settings
  ],

  // 👨‍💼 SUPERVISOR: Cuentas, nueva captura, encuestas, teléfono y supervisión WhatsApp
  [UserRole.SUPERVISOR]: [
    Permission.VIEW_ACCOUNTS,
    Permission.CREATE_ENTRY,
    Permission.MANAGE_SURVEY_TEMPLATES,
    Permission.VIEW_PHONE_DASHBOARD,
    Permission.VIEW_WHATSAPP_INBOX,
    Permission.MANAGE_WHATSAPP_ASSIGNMENTS
  ],
  
  // 👑 ADMIN: Acceso completo (incluye módulo telefónico aunque no tenga rol PHONE_AGENT)
  [UserRole.ADMIN]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_CLIENTS,
    Permission.VIEW_REFERRALS,
    Permission.VIEW_CLAIMS,
    Permission.CREATE_ENTRY,
    Permission.ACCESS_SETTINGS,
    Permission.MANAGE_USERS,
    Permission.MANAGE_SPECIALISTS,
    Permission.MANAGE_SURVEY_TEMPLATES,
    Permission.VIEW_PHONE_DASHBOARD,
    Permission.REGISTER_PHONE_CALL,
    Permission.VIEW_OWN_PHONE_CALLS,
    Permission.ASSIGN_SPECIALIST,
    Permission.UPLOAD_EVIDENCE,
    Permission.VIEW_WHATSAPP_INBOX,
    Permission.MANAGE_WHATSAPP_ASSIGNMENTS
  ],

  // 🔄 COMPSYCH_SYNC: Dashboard EAP + cuentas, clientes, casos y citas (sin teléfono ni administración)
  [UserRole.COMPSYCH_SYNC]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ACCOUNTS,
    Permission.VIEW_CLIENTS,
    Permission.VIEW_REFERRALS,
    Permission.VIEW_CLAIMS
  ]
};

const ROLE_NAME_ALIASES: Record<string, UserRole> = {
  COMPSYCH: UserRole.COMPSYCH_SYNC
};

/**
 * Convierte el string de rol del backend a una clave de ROLE_PERMISSIONS.
 * Soporta variantes comunes: ROLE_XXX, guiones, mayúsculas/mixtas.
 */
export const normalizeRoleKey = (raw: string): UserRole | null => {
  const t = raw.trim().toUpperCase().replace(/-/g, '_').replace(/^ROLE_/, '');
  if (ROLE_NAME_ALIASES[t]) return ROLE_NAME_ALIASES[t];
  if (Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, t)) {
    return t as UserRole;
  }
  return null;
};

// ✅ NUEVO: Helper para verificar si usuario tiene permiso
export const hasPermission = (user: User, permission: Permission): boolean => {
  return user.roles.some((role) => {
    const key = normalizeRoleKey(role);
    if (!key) return false;
    return ROLE_PERMISSIONS[key]?.includes(permission) ?? false;
  });
};

/** Primera ruta a la que puede entrar el usuario (post-login, índice, acceso denegado). */
const DEFAULT_HOME_BY_PERMISSION: { permission: Permission; path: string }[] = [
  { permission: Permission.VIEW_DASHBOARD, path: '/dashboard' },
  { permission: Permission.VIEW_PHONE_DASHBOARD, path: '/phone-dashboard' },
  { permission: Permission.VIEW_CLIENTS, path: '/clients' },
  { permission: Permission.VIEW_ACCOUNTS, path: '/accounts' },
  { permission: Permission.VIEW_REFERRALS, path: '/referrals' },
  { permission: Permission.VIEW_CLAIMS, path: '/claims' },
  { permission: Permission.CREATE_ENTRY, path: '/new-entry' },
  { permission: Permission.MANAGE_USERS, path: '/users' },
  { permission: Permission.MANAGE_SPECIALISTS, path: '/specialists' },
  { permission: Permission.MANAGE_SURVEY_TEMPLATES, path: '/survey-templates' },
  { permission: Permission.ACCESS_SETTINGS, path: '/settings' }
];

export const getDefaultHomePath = (user: User): string => {
  for (const { permission, path } of DEFAULT_HOME_BY_PERMISSION) {
    if (hasPermission(user, permission)) return path;
  }
  return '/login';
};

// ✅ NUEVO: Helper para verificar si usuario tiene rol
export const hasRole = (user: User, role: UserRole): boolean => {
  return user.roles.some((r) => normalizeRoleKey(r) === role);
};

// ✅ NUEVO: Helper para verificar si usuario es admin
export const isAdmin = (user: User): boolean => {
  return hasRole(user, UserRole.ADMIN);
};

// ✅ NUEVO: Helper para verificar si es agente telefónico
export const isPhoneAgent = (user: User): boolean => {
  return hasRole(user, UserRole.PHONE_AGENT);
};

// ✅ NUEVO: Helper para verificar si usuario necesita establecer contraseña
export const needsPasswordSetup = (user: User): boolean => {
  return !user.passwordSet;
};

// ✅ NUEVO: Helper para formatear fechas
export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Nunca';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};